import React, { useState } from "react";
import { ChevronDown, ChevronUp, Trophy, ClipboardList, ShoppingCart, Plane, CalendarDays } from "lucide-react";

const formatShortDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [h, min] = timeStr.substring(0, 5).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(min).padStart(2, "0")} ${ampm}`;
};

const WeekAtAGlance = ({
  events = [],
  chores = [],
  shoppingItems = [],
  vacationBlocks = [],
  seasons = [],
  onGoToChores,
  onGoToShopping,
  onGoToDate,
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("fc_glance_collapsed") === "true"; } catch { return false; }
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("fc_glance_collapsed", String(next)); } catch {}
  };

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const dayOfWeek = today.getDay();
  const weekEnd = new Date(today);
  weekEnd.setDate(today.getDate() + (6 - dayOfWeek));
  const weekEndStr = weekEnd.toISOString().slice(0, 10);

  // Next upcoming game
  const nextGame = events
    .filter(e => e.season_id && !e.canceled && !e._virtual && e.date >= todayStr)
    .sort((a, b) => a.date > b.date ? 1 : a.date < b.date ? -1 : (a.time || "").localeCompare(b.time || ""))
    [0];
  const nextGameSeason = nextGame ? seasons.find(s => s.id === nextGame.season_id) : null;

  // Non-game events remaining this week
  const thisWeekEvents = events.filter(e =>
    !e._virtual && !e.canceled && !e.season_id &&
    e.date >= todayStr && e.date <= weekEndStr
  );

  // Chores
  const overdueChores = chores.filter(c => !c.completed && c.due_date && c.due_date < todayStr);
  const dueThisWeekChores = chores.filter(c => !c.completed && c.due_date && c.due_date >= todayStr && c.due_date <= weekEndStr);
  const totalChoresDue = overdueChores.length + dueThisWeekChores.length;

  // Shopping
  const uncheckedCount = shoppingItems.filter(i => !i.checked).length;

  // Active trips
  const activeTrips = vacationBlocks.filter(v => v.start_date <= weekEndStr && v.end_date >= todayStr);

  const isEmpty = !nextGame && thisWeekEvents.length === 0 && totalChoresDue === 0 && uncheckedCount === 0 && activeTrips.length === 0;

  if (collapsed) {
    return (
      <div
        className="bg-white rounded-lg shadow px-4 py-2.5 mb-3 flex items-center justify-between cursor-pointer"
        onClick={toggle}
      >
        <span className="text-sm font-semibold text-gray-700">This Week</span>
        <div className="flex items-center gap-3 text-xs">
          {nextGame && <span className="text-yellow-600 font-medium">🏆 {formatShortDate(nextGame.date)}</span>}
          {totalChoresDue > 0 && (
            <span className={overdueChores.length > 0 ? "text-red-500 font-medium" : "text-orange-500 font-medium"}>
              ✅ {totalChoresDue}
            </span>
          )}
          {uncheckedCount > 0 && <span className="text-blue-500 font-medium">🛒 {uncheckedCount}</span>}
          {activeTrips.length > 0 && <span className="text-teal-500 font-medium">✈️ Trip</span>}
          <ChevronDown size={15} className="text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-3 mb-3">
      <div className="flex items-center justify-between mb-2.5 cursor-pointer" onClick={toggle}>
        <span className="text-sm font-semibold text-gray-700">This Week at a Glance</span>
        <ChevronUp size={15} className="text-gray-400" />
      </div>

      {isEmpty ? (
        <p className="text-sm text-gray-400 py-1">Nothing on the schedule this week.</p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1 -mb-1">

          {/* Next Game */}
          {nextGame && (
            <button
              onClick={() => onGoToDate(nextGame.date)}
              className="flex-shrink-0 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-left min-w-[130px] active:bg-yellow-100"
            >
              <div className="flex items-center gap-1 text-yellow-700 text-xs font-semibold mb-1">
                <Trophy size={11} />
                <span>{nextGameSeason?.sport || nextGameSeason?.name || "Game"}</span>
              </div>
              <div className="text-xs font-medium text-gray-800 truncate max-w-[120px]">{nextGame.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {formatShortDate(nextGame.date)}{nextGame.time ? ` · ${formatTime(nextGame.time)}` : ""}
              </div>
            </button>
          )}

          {/* Events this week */}
          {thisWeekEvents.length > 0 && (
            <button
              onClick={() => onGoToDate(todayStr)}
              className="flex-shrink-0 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-left min-w-[100px] active:bg-purple-100"
            >
              <div className="flex items-center gap-1 text-purple-700 text-xs font-semibold mb-1">
                <CalendarDays size={11} />
                <span>Events</span>
              </div>
              <div className="text-xl font-bold text-purple-700 leading-none">{thisWeekEvents.length}</div>
              <div className="text-xs text-gray-500 mt-0.5">this week</div>
            </button>
          )}

          {/* Chores */}
          {totalChoresDue > 0 && (
            <button
              onClick={onGoToChores}
              className={`flex-shrink-0 border rounded-lg px-3 py-2 text-left min-w-[100px] active:opacity-80 ${
                overdueChores.length > 0 ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"
              }`}
            >
              <div className={`flex items-center gap-1 text-xs font-semibold mb-1 ${overdueChores.length > 0 ? "text-red-700" : "text-orange-700"}`}>
                <ClipboardList size={11} />
                <span>Chores</span>
              </div>
              <div className={`text-xl font-bold leading-none ${overdueChores.length > 0 ? "text-red-700" : "text-orange-700"}`}>
                {totalChoresDue}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {overdueChores.length > 0 ? `${overdueChores.length} overdue` : "due this week"}
              </div>
            </button>
          )}

          {/* Shopping */}
          {uncheckedCount > 0 && (
            <button
              onClick={onGoToShopping}
              className="flex-shrink-0 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-left min-w-[100px] active:bg-blue-100"
            >
              <div className="flex items-center gap-1 text-blue-700 text-xs font-semibold mb-1">
                <ShoppingCart size={11} />
                <span>Shopping</span>
              </div>
              <div className="text-xl font-bold text-blue-700 leading-none">{uncheckedCount}</div>
              <div className="text-xs text-gray-500 mt-0.5">item{uncheckedCount !== 1 ? "s" : ""} needed</div>
            </button>
          )}

          {/* Active trips */}
          {activeTrips.map(trip => (
            <button
              key={trip.id}
              onClick={() => onGoToDate(trip.start_date)}
              className="flex-shrink-0 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-left min-w-[130px] active:bg-teal-100"
            >
              <div className="flex items-center gap-1 text-teal-700 text-xs font-semibold mb-1">
                <Plane size={11} />
                <span>Trip</span>
              </div>
              <div className="text-xs font-medium text-gray-800 truncate max-w-[120px]">{trip.label || "Trip"}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {trip.start_date === trip.end_date
                  ? formatShortDate(trip.start_date)
                  : `${formatShortDate(trip.start_date)} – ${formatShortDate(trip.end_date)}`}
              </div>
            </button>
          ))}

        </div>
      )}
    </div>
  );
};

export default WeekAtAGlance;
